import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
  ShieldCheckIcon,
  EyeIcon,
  ChevronUpIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import AdminLayout from '../components/AdminLayout';
import Pagination from '../components/Pagination';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Risk level badge component
const RiskLevelBadge = ({ level }) => {
  const colors = {
    low: 'bg-green-100 text-green-800',
    medium: 'bg-yellow-100 text-yellow-800',
    high: 'bg-orange-100 text-orange-800',
    critical: 'bg-red-100 text-red-800'
  };
  
  return (
    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${colors[level] || colors.low}`}>
      {level?.charAt(0).toUpperCase() + level?.slice(1) || 'Unknown'}
    </span>
  );
};

// Risk score bar
const RiskScoreBar = ({ score }) => {
  const getColor = (s) => {
    if (s >= 80) return 'bg-red-500';
    if (s >= 60) return 'bg-orange-500';
    if (s >= 30) return 'bg-yellow-500';
    return 'bg-green-500';
  };
  
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`h-full ${getColor(score)} rounded-full transition-all`}
          style={{ width: `${score}%` }}
        />
      </div>
      <span className="text-sm font-medium text-gray-700 w-8">{score}</span>
    </div>
  );
};

// Status badges
const StatusBadge = ({ type, active }) => {
  if (!active) return null;
  
  const badges = {
    flagged: { icon: ExclamationTriangleIcon, color: 'bg-yellow-100 text-yellow-700', label: 'Flagged' },
    blocked: { icon: NoSymbolIcon, color: 'bg-red-100 text-red-700', label: 'Blocked' },
    captcha: { icon: ShieldCheckIcon, color: 'bg-blue-100 text-blue-700', label: 'Captcha' },
    otp: { icon: ShieldCheckIcon, color: 'bg-purple-100 text-purple-700', label: 'OTP' }
  };
  
  const badge = badges[type];
  if (!badge) return null;
  
  const Icon = badge.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${badge.color}`}>
      <Icon className="w-3 h-3" />
      {badge.label}
    </span>
  );
};

const RiskUsers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [riskLevel, setRiskLevel] = useState(searchParams.get('riskLevel') || '');
  const [isFlagged, setIsFlagged] = useState(searchParams.get('isFlagged') || '');
  const [isBlocked, setIsBlocked] = useState(searchParams.get('isBlocked') || '');
  const [minScore, setMinScore] = useState(searchParams.get('minScore') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'risk_score');
  const [sortOrder, setSortOrder] = useState(searchParams.get('sortOrder') || 'desc');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        sortBy,
        sortOrder,
        ...(search && { search }),
        ...(riskLevel && { riskLevel }),
        ...(isFlagged && { isFlagged }),
        ...(isBlocked && { isBlocked }),
        ...(minScore && { minScore })
      });

      const response = await axios.get(`${API_BASE_URL}/api/admin/risk/users?${params}`);
      setUsers(response.data.data.users);
      setTotalPages(response.data.data.pagination.totalPages);
    } catch (error) {
      console.error('Error fetching risk users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    
    // Update URL params
    const params = new URLSearchParams();
    if (currentPage > 1) params.set('page', currentPage);
    if (search) params.set('search', search);
    if (riskLevel) params.set('riskLevel', riskLevel);
    if (isFlagged) params.set('isFlagged', isFlagged);
    if (isBlocked) params.set('isBlocked', isBlocked);
    if (minScore) params.set('minScore', minScore);
    if (sortBy !== 'risk_score') params.set('sortBy', sortBy);
    if (sortOrder !== 'desc') params.set('sortOrder', sortOrder);
    setSearchParams(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, riskLevel, isFlagged, isBlocked, minScore, sortBy, sortOrder]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchUsers();
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u.user_id));
    }
  };

  const handleSelectUser = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedUsers.length === 0) {
      toast.error('No users selected');
      return;
    }
    
    const reason = action === 'block' || action === 'flag' 
      ? window.prompt(`Enter reason for ${action}:`)
      : null;
    
    if ((action === 'block' || action === 'flag') && !reason) {
      toast.error('Reason is required');
      return;
    }
    
    try {
      const endpoint = action === 'block' ? 'block' : 
                       action === 'unblock' ? 'unblock' :
                       action === 'flag' ? 'flag' : 'unflag';
      
      for (const userId of selectedUsers) {
        await axios.post(`${API_BASE_URL}/api/admin/risk/users/${userId}/${endpoint}`, { reason });
      }
      
      toast.success(`${action} applied to ${selectedUsers.length} users`);
      setSelectedUsers([]);
      fetchUsers();
    } catch (error) {
      console.error(`Error performing bulk ${action}:`, error);
      toast.error(`Failed to ${action} users`);
    }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' 
      ? <ChevronUpIcon className="w-4 h-4 inline ml-1" />
      : <ChevronDownIcon className="w-4 h-4 inline ml-1" />;
  };

  return (
    <AdminLayout>
      <div>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Risk Users</h1>
            <p className="text-gray-600 mt-1">Manage users by risk profile</p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/risk"
              className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              ← Back to Dashboard
            </Link>
            <button
              onClick={fetchUsers}
              disabled={loading}
              className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1 min-w-64">
              <div className="relative">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by email, name, or IP..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </form>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 rounded-lg border transition-colors ${
                showFilters ? 'bg-primary-50 border-primary-300 text-primary-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <FunnelIcon className="w-5 h-5 inline mr-2" />
              Filters
            </button>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Risk Level</label>
                <select
                  value={riskLevel}
                  onChange={(e) => { setRiskLevel(e.target.value); setCurrentPage(1); }}
                  className="w-full border border-gray-300 rounded-lg p-2"
                >
                  <option value="">All Levels</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Flagged Status</label>
                <select
                  value={isFlagged}
                  onChange={(e) => { setIsFlagged(e.target.value); setCurrentPage(1); }}
                  className="w-full border border-gray-300 rounded-lg p-2"
                >
                  <option value="">All</option>
                  <option value="true">Flagged Only</option>
                  <option value="false">Not Flagged</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Blocked Status</label>
                <select
                  value={isBlocked}
                  onChange={(e) => { setIsBlocked(e.target.value); setCurrentPage(1); }}
                  className="w-full border border-gray-300 rounded-lg p-2"
                >
                  <option value="">All</option>
                  <option value="true">Blocked Only</option>
                  <option value="false">Not Blocked</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min Risk Score</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={minScore}
                  onChange={(e) => { setMinScore(e.target.value); setCurrentPage(1); }}
                  placeholder="0-100"
                  className="w-full border border-gray-300 rounded-lg p-2"
                />
              </div>
            </div>
          )}
        </div>

        {/* Bulk Actions */}
        {selectedUsers.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-4 mb-4 flex items-center justify-between">
            <span className="text-sm text-gray-600">
              {selectedUsers.length} user{selectedUsers.length > 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleBulkAction('flag')}
                className="px-3 py-1.5 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 text-sm"
              >
                Flag Selected
              </button>
              <button
                onClick={() => handleBulkAction('unflag')}
                className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm"
              >
                Unflag Selected
              </button>
              <button
                onClick={() => handleBulkAction('block')}
                className="px-3 py-1.5 bg-red-100 text-red-700 rounded hover:bg-red-200 text-sm"
              >
                Block Selected
              </button>
              <button
                onClick={() => handleBulkAction('unblock')}
                className="px-3 py-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200 text-sm"
              >
                Unblock Selected
              </button>
              <button
                onClick={() => setSelectedUsers([])}
                className="px-3 py-1.5 text-gray-500 hover:text-gray-700 text-sm"
              >
                Clear Selection
              </button>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedUsers.length === users.length && users.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('risk_score')}
                >
                  Risk Score <SortIcon field="risk_score" />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Level
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  IP
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('created_at')}
                >
                  Created <SortIcon field="created_at" />
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    </div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.user_id} className={`hover:bg-gray-50 ${user.is_blocked ? 'bg-red-50' : ''}`}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.user_id)}
                        onChange={() => handleSelectUser(user.user_id)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-sm font-medium">
                          {user.name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{user.name || 'No name'}</p>
                          <p className="text-sm text-gray-500">{user.email || 'No email'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <RiskScoreBar score={user.risk_score || 0} />
                    </td>
                    <td className="px-4 py-3">
                      <RiskLevelBadge level={user.risk_level} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        <StatusBadge type="flagged" active={user.is_flagged} />
                        <StatusBadge type="blocked" active={user.is_blocked} />
                        <StatusBadge type="captcha" active={user.requires_captcha} />
                        <StatusBadge type="otp" active={user.requires_email_otp} />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {user.last_seen_ip || user.first_seen_ip || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {user.user_created_at ? new Date(user.user_created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/risk/users/${user.user_id}`}
                        className="text-primary-600 hover:text-primary-700"
                      >
                        <EyeIcon className="w-5 h-5 inline" />
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default RiskUsers;
